import {
    useState
} from "react";
import {
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Separator,
    Stepper,
    Button,
    Badge,
    Card,
    Item,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    MessageCircle,
    ChevronDown,
    ChevronLeft,
    ShoppingBag,
    CircleHelp,
    ChevronUp,
    Utensils,
    House,
    Phone
} from "lucide-react-native";
import {
    Polyline,
    Circle,
    Rect,
    Path,
    Svg,
    G
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

type Road = {
    outer: number;
    inner: number;
    d: string;
};

type Block = {
    height: number;
    width: number;
    x: number;
    y: number;
};

const ChevronLeftIcon = createKitIcon(ChevronLeft);

const HelpIcon = createKitIcon(CircleHelp);

const PhoneIcon = createKitIcon(Phone);

const BagIcon = createKitIcon(ShoppingBag);

const MessageIcon = createKitIcon(MessageCircle);

const MAP_WIDTH = 390;

const MAP_HEIGHT = 470;

const STREET_XS = [
    60,
    150,
    250,
    340,
    390
];

const STREET_YS = [
    90,
    190,
    280,
    360,
    470
];

const BLOCK_INSET = 10;

const BLOCKS: Array<Block> = STREET_XS.slice(0, -1).flatMap((x, column) => STREET_YS.slice(0, -1).flatMap((y, row) => {
    if(column === 1 && row === 0) {
        return [];
    }

    return [
        {
            width: (STREET_XS[column + 1] ?? x) - x - BLOCK_INSET * 2,
            height: (STREET_YS[row + 1] ?? y) - y - BLOCK_INSET * 2,
            x: x + BLOCK_INSET,
            y: y + BLOCK_INSET
        }
    ];
}));

const ROADS: Array<Road> = [
    {
        d: "M-10 90 H400",
        outer: 12,
        inner: 10
    },
    {
        d: "M-10 190 H400",
        outer: 8,
        inner: 6
    },
    {
        d: "M-10 280 H400",
        outer: 12,
        inner: 10
    },
    {
        d: "M-10 360 H400",
        outer: 8,
        inner: 6
    },
    {
        d: "M60 -10 V480",
        outer: 12,
        inner: 10
    },
    {
        d: "M150 -10 V480",
        outer: 8,
        inner: 6
    },
    {
        d: "M250 -10 V480",
        outer: 12,
        inner: 10
    },
    {
        d: "M340 -10 V480",
        outer: 8,
        inner: 6
    },
    {
        d: "M-20 440 L410 250",
        outer: 12,
        inner: 10
    }
];

const SEA_PATH = "M0 110 C 22 150 26 190 14 230 C 4 262 20 300 34 340 C 40 370 30 420 36 470 L0 470 Z";

const PARK_TREES = [
    {
        cx: 184,
        cy: 124,
        r: 9
    },
    {
        cx: 214,
        cy: 138,
        r: 12
    },
    {
        cx: 190,
        cy: 158,
        r: 8
    }
];

const ROUTE_DONE = "60,90 250,90 250,190";

const ROUTE_LEFT = "250,190 250,280 340,280 340,336";

const RESTAURANT = {
    x: 60,
    y: 90
};

const HOME = {
    x: 340,
    y: 336
};

const COURIER = {
    x: 250,
    y: 190
};

const STEPS = [
    {
        key: "received",
        time: "19:18"
    },
    {
        key: "preparing",
        time: "19:22"
    },
    {
        key: "onTheWay",
        time: "19:31"
    },
    {
        key: "delivered",
        time: "~19:42"
    }
];

const ITEMS = [
    {
        key: "flatWhite",
        price: 148
    },
    {
        key: "croissant",
        price: 96
    },
    {
        key: "cheesecake",
        price: 168
    }
];

const ACTIVE_STEP = 2;

const stylesheet = StyleSheet.create({
    controlsRow: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    summaryRow: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    eta: {
        justifyContent: "space-between",
        alignItems: "flex-end",
        flexDirection: "row"
    },
    controlsCenter: {
        justifyContent: "center",
        pointerEvents: "none",
        position: "absolute",
        alignItems: "center",
        bottom: 0,
        right: 0,
        left: 0,
        top: 0
    },
    pin: {
        justifyContent: "center",
        position: "absolute",
        alignItems: "center"
    },
    etaValue: {
        alignItems: "baseline",
        flexDirection: "row"
    },
    column: {
        flexDirection: "column"
    },
    sheet: {
        flexDirection: "column"
    },
    controls: {
        position: "absolute",
        right: 0,
        left: 0,
        top: 0
    },
    label: {
        position: "absolute"
    },
    screen: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    },
    handle: {
        alignSelf: "center"
    },
    map: {
        width: "100%"
    },
    grow: {
        minWidth: 0,
        flex: 1
    }
});

const handlePress = () => undefined;

const toPercent = (value: number, total: number) => `${value / total * 100}%` as const;

const Food01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        avatarSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isSummaryOpen,
        setIsSummaryOpen
    ] = useState(false);

    const currency = new Intl.NumberFormat(activeLocale, {
        minimumFractionDigits: 2,
        style: "currency",
        currency: "TRY"
    });

    const total = ITEMS.reduce((sum, item) => sum + item.price, 0);

    const handleToggleSummary = () => setIsSummaryOpen(!isSummaryOpen);

    const renderPin = (point: typeof HOME, icon: typeof House, size: number) => <View
        style={[
            stylesheet.pin,
            {
                backgroundColor: colors.content.container.inverse,
                left: toPercent(point.x, MAP_WIDTH),
                top: toPercent(point.y, MAP_HEIGHT),
                borderRadius: radiuses.full,
                marginLeft: -size / 2,
                marginTop: -size / 2,
                height: size,
                width: size
            }
        ]}
    >
        <Icon
            customColor={colors.content.container.default}
            icon={icon}
        />
    </View>;

    const renderLabel = (text: string, point: typeof HOME, offsetX: number, offsetY: number) => <Text
        style={[
            stylesheet.label,
            {
                left: toPercent(point.x + offsetX, MAP_WIDTH),
                top: toPercent(point.y + offsetY, MAP_HEIGHT)
            }
        ]}
        variant="labelSmallSize"
        color="high"
    >
        {text}
    </Text>;

    const renderItem = (item: typeof ITEMS[number]) => <View
        style={stylesheet.summaryRow}
        key={item.key}
    >
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {t(`blocks.food01.items.${item.key}`)}
        </Text>
        <Text
            variant="labelMediumSize"
            color="high"
        >
            {currency.format(item.price)}
        </Text>
    </View>;

    return <ScrollView
        contentContainerStyle={[
            stylesheet.screen,
            {
                maxWidth: shapeConfigs.sheetMaxWidth
            }
        ]}
    >
        <View
            style={[
                stylesheet.map,
                {
                    aspectRatio: MAP_WIDTH / MAP_HEIGHT
                }
            ]}
        >
            <Svg
                accessibilityLabel={t("blocks.food01.mapLabel")}
                viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
                preserveAspectRatio="xMidYMid slice"
                height="100%"
                width="100%"
            >
                <Rect
                    fill={colors.content.container.subtle}
                    height={MAP_HEIGHT}
                    width={MAP_WIDTH}
                />
                <Path
                    fill={colors.chart.categoricalArea["3"]}
                    d={SEA_PATH}
                />
                {
                    BLOCKS.map((block) => <Rect
                        fill={colors.content.container.mid}
                        key={`${block.x}-${block.y}`}
                        height={block.height}
                        width={block.width}
                        x={block.x}
                        y={block.y}
                        rx={6}
                    />)
                }
                <Rect
                    fill={colors.chart.categoricalArea["1"]}
                    height={80}
                    width={80}
                    x={160}
                    y={100}
                    rx={10}
                />
                {
                    PARK_TREES.map((tree) => <Circle
                        fill={colors.chart.categorical["1"]}
                        key={`${tree.cx}-${tree.cy}`}
                        opacity={0.35}
                        cx={tree.cx}
                        cy={tree.cy}
                        r={tree.r}
                    />)
                }
                {
                    ROADS.map((road) => <G
                        strokeLinecap="round"
                        key={road.d}
                        fill="none"
                    >
                        <Path
                            stroke={colors.content.border.default}
                            strokeWidth={road.outer}
                            d={road.d}
                        />
                        <Path
                            stroke={colors.content.container.default}
                            strokeWidth={road.inner}
                            d={road.d}
                        />
                    </G>)
                }
                <Polyline
                    stroke={colors.chart.categorical["3"]}
                    strokeWidth={borders.chartLine * 2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points={ROUTE_DONE}
                    opacity={0.35}
                    fill="none"
                />
                <Polyline
                    stroke={colors.chart.categorical["3"]}
                    strokeWidth={borders.chartLine * 2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points={ROUTE_LEFT}
                    fill="none"
                />
                <Circle
                    fill={colors.chart.categoricalArea["3"]}
                    cx={HOME.x}
                    cy={HOME.y}
                    r={20}
                />
                <Circle
                    fill={colors.chart.categorical["3"]}
                    cx={COURIER.x}
                    cy={COURIER.y}
                    opacity={0.25}
                    r={22}
                />
                <Circle
                    stroke={colors.content.container.default}
                    fill={colors.chart.categorical["3"]}
                    strokeWidth={3}
                    cx={COURIER.x}
                    cy={COURIER.y}
                    r={12}
                />
                <Circle
                    fill={colors.content.container.default}
                    cx={COURIER.x}
                    cy={COURIER.y}
                    r={3}
                />
            </Svg>
            {renderPin(RESTAURANT, Utensils, avatarSizes.xSmall)}
            {renderPin(HOME, House, avatarSizes.xSmall)}
            {renderLabel(t("blocks.food01.restaurant"), RESTAURANT, 24, 12)}
            {renderLabel(t("blocks.food01.home"), HOME, -16, 36)}
            <View
                style={[
                    stylesheet.controls,
                    {
                        padding: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={stylesheet.controlsRow}
                >
                    <Button
                        accessibilityLabel={t("blocks.food01.back")}
                        icon={ChevronLeftIcon}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        variant="tonal"
                        type="neutral"
                    />
                    <Button
                        accessibilityLabel={t("blocks.food01.help")}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        icon={HelpIcon}
                        variant="tonal"
                        type="neutral"
                    />
                </View>
                <View
                    style={[
                        stylesheet.controlsCenter,
                        {
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <Badge
                        title={t("blocks.food01.orderId")}
                        spreadBehaviour="free"
                        variant="tonal"
                        type="neutral"
                    />
                </View>
            </View>
        </View>
        <Card
            style={[
                stylesheet.sheet,
                {
                    marginTop: -radiuses.xl
                }
            ]}
            spreadBehaviour="stretch"
            variant="elevated"
        >
            <Card.Content>
                <View
                    style={[
                        stylesheet.column,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.handle,
                            {
                                backgroundColor: colors.content.icon.low,
                                borderRadius: radiuses.full,
                                height: borders.indicator,
                                width: spaces.spacingXxl
                            }
                        ]}
                    />
                    <View
                        style={stylesheet.eta}
                    >
                        <View
                            style={stylesheet.grow}
                        >
                            <Text
                                variant="titleMediumSize"
                                role="heading"
                                color="high"
                            >
                                {t("blocks.food01.title")}
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.food01.expected", [
                                    "19:42"
                                ])}
                            </Text>
                        </View>
                        <View
                            style={[
                                stylesheet.etaValue,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                variant="displaySmallSize"
                                color="high"
                            >
                                12
                            </Text>
                            <Text
                                variant="titleMediumSize"
                                color="mid"
                            >
                                {t("blocks.food01.minutes")}
                            </Text>
                        </View>
                    </View>
                    <Item>
                        <Item.Media
                            avatarProps={{
                                title: t("blocks.food01.courierFull")
                            }}
                            variant="avatar"
                        />
                        <Item.Content>
                            <Item.Title>
                                {t("blocks.food01.courier")}
                            </Item.Title>
                            <Item.Description>
                                {t("blocks.food01.courierInfo")}
                            </Item.Description>
                        </Item.Content>
                        <Item.Actions>
                            <Button
                                accessibilityLabel={t("blocks.food01.call")}
                                spreadBehaviour="free"
                                onPress={handlePress}
                                variant="outline"
                                icon={PhoneIcon}
                                type="neutral"
                            />
                            <Button
                                accessibilityLabel={t("blocks.food01.message")}
                                spreadBehaviour="free"
                                onPress={handlePress}
                                icon={MessageIcon}
                                variant="outline"
                                type="neutral"
                            />
                        </Item.Actions>
                    </Item>
                    <Separator/>
                    <Stepper
                        accessibilityLabel={t("blocks.food01.stepsLabel")}
                        steps={STEPS.map((step) => ({
                            title: t(`blocks.food01.steps.${step.key}`),
                            description: step.time,
                            key: step.key
                        }))}
                        activeIndex={ACTIVE_STEP}
                        orientation="vertical"
                        size="small"
                    />
                    <View
                        style={[
                            stylesheet.column,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Item
                            onPress={handleToggleSummary}
                            variant="filled"
                        >
                            <Item.Media
                                icon={BagIcon}
                                variant="icon"
                            />
                            <Item.Content>
                                <Item.Title>
                                    {t("blocks.food01.summary")}
                                </Item.Title>
                                <Item.Description>
                                    {t("blocks.food01.summaryInfo", [
                                        String(ITEMS.length)
                                    ])}
                                </Item.Description>
                            </Item.Content>
                            <Item.Actions>
                                <Text
                                    variant="labelMediumSize"
                                    color="high"
                                >
                                    {currency.format(total)}
                                </Text>
                                <Icon
                                    icon={isSummaryOpen ? ChevronUp : ChevronDown}
                                    color="mid"
                                />
                            </Item.Actions>
                        </Item>
                        {
                            isSummaryOpen ?
                                <View
                                    style={[
                                        stylesheet.column,
                                        {
                                            paddingHorizontal: spaces.spacingMd,
                                            gap: spaces.spacingSm
                                        }
                                    ]}
                                >
                                    {ITEMS.map(renderItem)}
                                </View>
                            :
                                null
                        }
                    </View>
                </View>
            </Card.Content>
        </Card>
    </ScrollView>;
};
export default Food01;
